/**
 * OfficeSwitcherContext
 * =====================
 *
 * Global "active office" filter for multi-office managers.
 *
 * - Reads/writes the ``?office=<id>`` query parameter so a manager can
 *   bookmark a specific office view.
 * - Loads ``/api/offices/my-accessible`` once per session so pages can
 *   render the switcher, driver's licence, and filter their data.
 * - Exposes ``selectedOfficeId`` (``null`` == "All my offices"), the full
 *   ``accessibleOffices`` list, and a ``setSelectedOfficeId`` mutator.
 *
 * The context is a passive filter — pages MUST opt-in by reading the
 * value and appending ``?office_id=<id>`` (or their equivalent) to
 * their list-endpoint calls. Backend endpoints that support this drill-
 * down should validate that the requested office is inside the user's
 * accessible set.
 */
import React, {
  createContext, useContext, useEffect, useMemo, useState, useCallback,
} from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';

const OfficeSwitcherContext = createContext({
  accessibleOffices: [],
  selectedOfficeId: null,
  setSelectedOfficeId: () => {},
  loading: false,
  isMultiOffice: false,
});

export function OfficeSwitcherProvider({ children }) {
  const { API, token, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [accessibleOffices, setAccessibleOffices] = useState([]);
  const [loading, setLoading] = useState(false);

  const urlOfficeId = searchParams.get('office') || null;

  const setSelectedOfficeId = useCallback((officeId) => {
    // ``null`` clears the filter (shows all accessible offices).
    const next = new URLSearchParams(searchParams);
    if (!officeId || officeId === 'all') {
      next.delete('office');
    } else {
      next.set('office', officeId);
    }
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!token || !user) return;
    // Only fetch for roles that could plausibly manage >1 office. Sales
    // reps always see their own scope so no switcher is needed.
    const eligibleRoles = ['admin', 'accounting', 'office_manager', 'regional_manager'];
    if (!eligibleRoles.includes(user.role)) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/offices/my-accessible`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        // Response shape: { offices: [{id, name, code, type}], all: bool }
        const list = Array.isArray(r.data?.offices) ? r.data.offices
          : Array.isArray(r.data) ? r.data
          : [];
        setAccessibleOffices(list);
      } catch {
        if (!cancelled) setAccessibleOffices([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API, token, user]);

  // If URL contains an office that isn't in the accessible set, clear it
  // (prevents a manager from bookmarking someone else's office).
  useEffect(() => {
    if (!urlOfficeId || accessibleOffices.length === 0) return;
    const isValid = accessibleOffices.some((o) => o.id === urlOfficeId);
    if (!isValid) {
      const next = new URLSearchParams(searchParams);
      next.delete('office');
      setSearchParams(next, { replace: true });
    }
     
  }, [urlOfficeId, accessibleOffices]);

  const isMultiOffice = accessibleOffices.filter((o) => o.type === 'office').length > 1;

  const value = useMemo(() => ({
    accessibleOffices,
    selectedOfficeId: urlOfficeId,
    setSelectedOfficeId,
    loading,
    isMultiOffice,
  }), [accessibleOffices, urlOfficeId, setSelectedOfficeId, loading, isMultiOffice]);

  return (
    <OfficeSwitcherContext.Provider value={value}>
      {children}
    </OfficeSwitcherContext.Provider>
  );
}

export function useOfficeSwitcher() {
  return useContext(OfficeSwitcherContext);
}
